import React from 'react';
import styled from 'styled-components';
import { isEqual, isNil } from 'lodash-es';

import PageLoader from '@/components/page-loader';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { useMediaQuery } from '@/components/utils/useQueryMedia';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchConfiguratorConfig } from '@/store/slices/configurator-config';
import {
  clearLogosSuggestions,
  fetchDomainsSuggestionsByCountry,
  fetchDomainsSuggestionsByDomain,
  fetchLogo,
  fetchThemes,
} from '@/store/slices/ml-services';
import { writePendingBrandSessionId } from '@/store/slices/casino-instance';
import { clearDomainConnect, sessionDeploy, sessionSave } from '@/store/slices/session';

import { useBrowserHistorySync, useDomainConnect, useIframe, useLogoUpload, useSession } from './hooks';
import { CountryListItem, CustomizerSessionState, DeployProps, DeployState } from './types';
import { areStatesEqual, findExtraElement, getCircularSlice, getValuesFromTokens, validateDomain } from './utils';

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const Deploy = (componentProps: DeployProps) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const dispatch = useAppDispatch();
  const domainsSuggestionsByCountry = useAppSelector((state) => state.mlServices.domainsSuggestionsByCountry);
  const domainsSuggestionsByDomain = useAppSelector((state) => state.mlServices.domainsSuggestionsByDomain);
  const logosSuggestionsCount = useAppSelector((state) => state.mlServices.logosSuggestionsCount);
  const logosSuggestions = useAppSelector((state) => state.mlServices.logosSuggestions);
  const mlServiceLoading = useAppSelector((state) => state.mlServices.loading);
  const loadingDomainsByCountry = useAppSelector((state) => state.mlServices.loadingDomainsByCountry);
  const loadingDomainsByDomain = useAppSelector((state) => state.mlServices.loadingDomainsByDomain);
  const loadingLogos = useAppSelector((state) => state.mlServices.loadingLogos);
  const logoThemes = useAppSelector((state) => state.mlServices.logoThemes);
  const logoOptions = useAppSelector((state) => state.mlServices.logoOptions);
  const session = useAppSelector((state) => state.session, isEqual);
  const configuratorConfig = useAppSelector((state) => state.configuratorConfig.config);
  const isMobile = useMediaQuery('(max-width: 899px)');

  React.useEffect(() => {
    dispatch(fetchConfiguratorConfig());
  }, [dispatch]);

  const countries = React.useMemo<CountryListItem[]>(() => {
    if (isNil(configuratorConfig)) {
      return [];
    }

    const newCountries: CountryListItem[] = [];

    const result = configuratorConfig.operating_region_groups
      .filter((regionGroup) => regionGroup.is_active)
      .map((regionGroup) => {
        const groupCountries = configuratorConfig.operating_regions
          .filter((region) => regionGroup.maps_service_identifier.operating_region_ids.includes(region.code))
          .toSorted((a, b) => a.priority - b.priority);

        return {
          ...regionGroup,
          regions: groupCountries,
        };
      })
      .toSorted((a, b) => a.priority - b.priority);

    result.forEach((regionGroup, regionGroupIndex) => {
      newCountries.push({
        type: 'header',
        text: regionGroup?.art_bundle?.[0]?.text_name ?? {},
      });

      regionGroup.regions.forEach((region) => {
        newCountries.push({
          type: 'region',
          text: region?.art_bundle?.[0]?.text_name ?? {},
          logo: region?.art_bundle?.[0]?.logo ?? '',
          currency: region.currency?.currency_code ?? '',
          code: region.code,
          disabled: region?.art_bundle?.[0]?.properties?.comingSoon ?? false,
        });
      });

      if (regionGroupIndex < result.length - 1) {
        newCountries.push({
          type: 'sep',
        });
      }
    });

    return newCountries;
  }, [configuratorConfig]);

  const [state, setState] = React.useState<DeployState>(() => ({
    loading: true,
    step: 1,
    totalSteps: 3,
    steps: [{ status: 'current' }, { status: 'disabled' }, { status: 'disabled' }],
    selectedCountry: null,
    selectedName: null,
    selectedDomain: null,
    selectedLogo: null,
    step2SelectBrand: false,
    step2SelectDomain: false,
    aiSelect: true,
    aiSelectLogo: true,
    uploadedFile: null,
    showDnsRecords: false,
    displayIntro: isMobile,
    step2Sub: '1a',
  }));

  useSession(session);

  useIframe(state.selectedLogo);

  const { onConnect, onOpenConnect, isConnectOpen } = useDomainConnect();

  React.useEffect(() => {
    if (!isNil(session.sessionId)) {
      dispatch(fetchThemes());
    }
  }, [dispatch, session.sessionId]);

  React.useEffect(() => {
    if (session.loaded && state.loading && countries.length > 0) {
      setState((prevState) => ({
        ...prevState,
        loading: false,
      }));
    }
  }, [session.loaded, state.loading, countries]);

  React.useEffect(() => {
    const sessionState: Partial<DeployState> = {
      selectedName: session.data.customization_data?.brandName ?? null,
      selectedCountry: session.data.customization_data?.country ?? session.data.configuration_data?.countryCode ?? null,
      selectedDomain: session.data.customization_data?.domain ?? null,
      selectedLogo: session.data.customization_data?.logo ?? null,
      aiSelect: !(session.data.customization_data?.own_name ?? session.data.customization_data?.own_domain ?? false),
      step2SelectBrand: session.data.customization_data?.own_name ?? false,
      step2SelectDomain: session.data.customization_data?.own_domain ?? false,
      aiSelectLogo: !(session.data.customization_data?.own_logo ?? false),
    };

    setState((prevState) => ({
      ...prevState,
      ...sessionState,
    }));
  }, [
    session.data.customization_data?.brandName,
    session.data.customization_data?.country,
    session.data.customization_data?.domain,
    session.data.customization_data?.logo,
    session.data.customization_data?.own_name,
    session.data.customization_data?.own_domain,
    session.data.customization_data?.own_logo,
    session.data.configuration_data?.countryCode,
  ]);

  React.useEffect(() => {
    const { selectedCountry, selectedName, selectedDomain, selectedLogo } = state;

    const sessionState: CustomizerSessionState = {
      brandName: selectedName ?? undefined,
      country: selectedCountry ?? undefined,
      domain: selectedDomain ?? undefined,
      logo: selectedLogo ?? undefined,
      own_name: state.step2SelectBrand,
      own_domain: state.step2SelectDomain,
      own_logo: !state.aiSelectLogo,
    };

    if (!areStatesEqual(session.data.customization_data, sessionState)) {
      const isNameChanged = session.data.customization_data?.brandName !== sessionState.brandName;
      const isDomainChanged = session.data.customization_data?.domain !== sessionState.domain;
      const needsDebounce = isNameChanged || isDomainChanged;

      const saveData = () => {
        if (isNil(session.sessionId)) {
          return;
        }

        dispatch(
          sessionSave({
            id: session.sessionId,
            data: {
              ...session.data,
              customization_data: {
                ...session.data.customization_data,
                ...sessionState,
              },
            },
          }),
        );
      };

      if (needsDebounce) {
        const saveSessionTimeoutId = setTimeout(saveData, 500);

        return () => clearTimeout(saveSessionTimeoutId);
      } else {
        saveData();
      }
    }
  }, [dispatch, session.data, session.sessionId, state]);

  const requestedDomainsByCountry = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (state.step === 2 && state.selectedCountry && requestedDomainsByCountry.current !== state.selectedCountry) {
      requestedDomainsByCountry.current = state.selectedCountry;
      dispatch(fetchDomainsSuggestionsByCountry({ country: state.selectedCountry }));
    }
  }, [state.step, state.selectedCountry, dispatch]);

  const requestedThemesSliceStartIndex = React.useRef(0);
  const requestedThemesSliceWrapped = React.useRef(false);
  const fetchLogoSuggestions = React.useCallback(() => {
    requestedLogos.current = true;
    const creativeTheme = findExtraElement(logoThemes, logoOptions);

    if (!creativeTheme) {
      return;
    }

    const { nextSlice, nextStartIndex } = getCircularSlice(
      logoThemes,
      logosSuggestionsCount - 1,
      requestedThemesSliceStartIndex.current,
    );
    const nextThemes = [...nextSlice, creativeTheme];
    const tokenValues = getValuesFromTokens(session.data.configuration_data?.tokens);

    if (tokenValues) {
      nextThemes.forEach((theme) => {
        if (isNil(state.selectedName)) {
          return;
        }

        dispatch(
          fetchLogo({
            brandName: state.selectedName,
            primaryColor: tokenValues.primaryColor,
            secondaryColor: tokenValues.secondaryColor,
            background: tokenValues.background,
            theme,
            bypassCache: requestedThemesSliceWrapped.current,
            // Server-side removal: the websocket delivers the transparent cutout
            // directly (cached end-to-end), so no per-logo /remove-background call.
            removeBackground: true,
          }),
        );
      });
    }

    if (nextStartIndex < requestedThemesSliceStartIndex.current) {
      requestedThemesSliceWrapped.current = true;
    }

    requestedThemesSliceStartIndex.current = nextStartIndex;
  }, [
    dispatch,
    logoOptions,
    logoThemes,
    logosSuggestionsCount,
    session.data.configuration_data?.tokens,
    state.selectedName,
  ]);

  const requestedLogos = React.useRef(false);
  React.useEffect(() => {
    if (state.step === 3 && !requestedLogos.current) {
      fetchLogoSuggestions();
    }
  }, [fetchLogoSuggestions, state.step]);

  // Keep `displayIntro` in sync with the viewport: show the intro when the
  // viewport becomes mobile, hide it when it becomes desktop.
  React.useEffect(() => {
    setState((prevState) =>
      prevState.displayIntro === isMobile ? prevState : { ...prevState, displayIntro: isMobile },
    );
  }, [isMobile]);

  const onNextStep = React.useCallback(() => {
    setState((prevState) => {
      // Dismiss the mobile intro screen instead of advancing the wizard.
      if (prevState.step === 1 && prevState.displayIntro && isMobile) {
        return { ...prevState, displayIntro: false };
      }

      const nextStep = prevState.step + 1;
      if (nextStep > prevState.totalSteps + 1) {
        return prevState;
      }

      const steps = prevState.steps.map((s) => ({ ...s }));
      steps[prevState.step - 1].status = 'ok';
      if (steps[nextStep - 1]) {
        if (nextStep - 1 === 1 && prevState.selectedCountry) {
          steps[nextStep - 1].status = 'ok';
        } else if (nextStep - 1 === 2 && prevState.selectedName && prevState.selectedDomain) {
          steps[nextStep - 1].status = 'ok';
        } else {
          steps[nextStep - 1].status = 'current';
        }
      }

      // When entering step 2 on mobile, start at the first sub-step of the
      // active subflow: '1a' (AI) or '2a' (manual own).
      let step2Sub = prevState.step2Sub;
      if (nextStep === 2 && isMobile) {
        step2Sub = prevState.aiSelect ? '1a' : '2a';
      }

      return { ...prevState, step: nextStep, steps, step2Sub };
    });
  }, [isMobile]);

  const onPrevStep = React.useCallback(() => {
    setState((prevState) => {
      // On mobile, going back from step 1 re-opens the intro screen.
      if (prevState.step === 1 && !prevState.displayIntro && isMobile) {
        return { ...prevState, displayIntro: true };
      }

      // Step 3 is a single screen — its AI/manual identity is a toggle, not a
      // navigable sub-screen — so back from step 3 retreats to step 2's last
      // substep (handled below), never to a step-3 sub-screen.

      // Step 2 sub-step back navigation (mobile only).
      //
      // Subflow B reverse: 2c → 2b → 2a → (exit to step 1)
      // Subflow A reverse: 1b → 1a → (exit to step 1)
      if (prevState.step === 2 && isMobile) {
        // Leaving the domain-connect substep (2b3 / '2c') in a single back
        // press: retreat to the manual-domain substep (2b2 / '2b') and abandon
        // any in-progress domain connection. The connect popup itself never
        // touches history (see wizard-navigation.md §6).
        if (prevState.step2Sub === '2c') {
          dispatch(clearDomainConnect());
          return { ...prevState, step2Sub: '2b', step2SelectDomain: false, showDnsRecords: false };
        }
        if (prevState.step2Sub === '2b') {
          return { ...prevState, step2Sub: '2a' };
        }
        if (prevState.step2Sub === '1b') {
          return { ...prevState, step2Sub: '1a' };
        }
        // '1a' / '2a' — first substep: fall through to retreat to step 1.
      }

      // Desktop step 2: handle step2SelectDomain cleanup before retreating.
      if (prevState.step === 2 && !isMobile && prevState.step2SelectDomain) {
        dispatch(clearDomainConnect());
        return { ...prevState, step2SelectDomain: false, selectedDomain: null, showDnsRecords: false };
      }

      const prevStep = prevState.step - 1;
      if (prevStep < 1) {
        return prevState;
      }

      const steps = prevState.steps.map((s) => ({ ...s }));
      if (steps[prevState.step - 1]) steps[prevState.step - 1].status = 'disabled';
      if (steps[prevStep - 1]) {
        if (prevStep - 1 === 0 && prevState.selectedCountry) {
          steps[prevStep - 1].status = 'ok';
        } else {
          steps[prevStep - 1].status = 'current';
        }
      }

      // When navigating back into step 2 from step 3, keep the substep the user
      // actually left from — onStep2Next preserves step2Sub when it advances, so
      // it already holds the correct screen ('1b' for subflow A; '2b' or '2c'
      // for subflow B, depending on whether the optional domain-connect detour
      // was taken). On desktop the substep doesn't drive anything (all DIVs show).
      return { ...prevState, step: prevStep, steps };
    });
  }, [dispatch, isMobile]);

  // ── Step-2 dedicated sub-step handlers (mobile only) ────────────────────────
  //
  // Subflow A (aiSelect === true):  1a → 1b → step 3
  // Subflow B (aiSelect === false): 2a → 2b → step 3
  //                                          └─(optional)→ 2c → step 3
  //
  // onStep2Next advances within the subflow and exits to step 3 from 1b / 2b / 2c.
  // The domain-connect substep 2c is an OPTIONAL detour entered ONLY via
  // onStep2SelectDomain (the "connect a domain" button on 2b); it is a real
  // history slot and stays put until back navigation or that button changes it.

  const onStep2Next = React.useCallback(() => {
    setState((prevState) => {
      // Subflow A: 1a → 1b
      if (prevState.step2Sub === '1a') {
        return { ...prevState, step2Sub: '1b' };
      }
      // Subflow B: 2a → 2b
      if (prevState.step2Sub === '2a') {
        return { ...prevState, step2Sub: '2b' };
      }

      // Any remaining substep ('1b', '2b', '2c') exits step 2 → main step 3.
      // The domain-connect substep (2c / 2b3) is an OPTIONAL detour reached only
      // via onStep2SelectDomain; the generic next from 2b goes straight to step 3,
      // and next from 2c likewise advances. 2c stays in history until a button
      // (back / onStep2SelectDomain) changes it.
      const nextStep = prevState.step + 1;
      if (nextStep > prevState.totalSteps + 1) {
        return prevState;
      }
      const steps = prevState.steps.map((s) => ({ ...s }));
      steps[prevState.step - 1].status = 'ok';
      if (steps[nextStep - 1]) {
        if (nextStep - 1 === 2 && prevState.selectedName && prevState.selectedDomain) {
          steps[nextStep - 1].status = 'ok';
        } else {
          steps[nextStep - 1].status = 'current';
        }
      }
      return { ...prevState, step: nextStep, steps };
    });
  }, []);

  const onSelectedCountry = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const countryId = event?.currentTarget?.dataset?.id ?? null;

      if (countryId) {
        setState((prevState) => {
          const steps = prevState.steps.map((s) => ({ ...s }));
          steps[0].status = 'ok';

          if (prevState.selectedCountry !== countryId) {
            steps[1].status = 'disabled';
            steps[2].status = 'disabled';
            requestedLogos.current = false;
            prevState.selectedName = null;
            prevState.selectedDomain = null;
            prevState.showDnsRecords = false;
            prevState.selectedLogo = null;
            requestedThemesSliceStartIndex.current = 0;
            requestedThemesSliceWrapped.current = false;
            setTimeout(() => {
              dispatch(clearLogosSuggestions());
              dispatch(clearDomainConnect());
            }, 0);
          }

          return { ...prevState, selectedCountry: countryId, steps };
        });
      }
    },
    [dispatch],
  );

  const onSelectName = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const name = event?.currentTarget?.dataset?.name ?? null;

      if (name) {
        setState((prevState) => {
          const steps = prevState.steps.map((s) => ({ ...s }));
          if (prevState?.selectedDomain) steps[1].status = 'ok';
          if (name !== prevState.selectedName) {
            steps[2].status = 'disabled';
            requestedLogos.current = false;
            prevState.selectedLogo = null;
            requestedThemesSliceStartIndex.current = 0;
            requestedThemesSliceWrapped.current = false;
            setTimeout(() => {
              dispatch(clearLogosSuggestions());
            }, 0);
          }
          return { ...prevState, selectedName: name, step2SelectBrand: false, selectedDomain: null, steps };
        });
      }
    },
    [dispatch],
  );

  const onSelectDomain = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    const domain = event?.currentTarget?.dataset?.domain ?? null;

    if (domain) {
      setState((prevState) => {
        const steps = prevState.steps.map((s) => ({ ...s }));
        if (prevState?.selectedName) steps[1].status = 'ok';
        return { ...prevState, selectedDomain: domain, steps };
      });
    }
  }, []);

  const onRefreshSuggestions = React.useCallback(() => {
    if (isNil(state.selectedCountry)) {
      return;
    }

    dispatch(fetchDomainsSuggestionsByCountry({ country: state.selectedCountry }));
  }, [dispatch, state.selectedCountry]);

  const onAiSelectToggle = React.useCallback(() => {
    // Flipping step 2's subflow resets the *entire* step (landing on the first
    // substep of the new subflow) and cascades a reset to the following steps
    // (3 and 4). Applies on both layouts. See wizard-navigation.md §5.
    requestedLogos.current = false;
    requestedThemesSliceStartIndex.current = 0;
    requestedThemesSliceWrapped.current = false;
    dispatch(clearDomainConnect());
    dispatch(clearLogosSuggestions());

    setState((prevState) => {
      const newAiSelect = !prevState.aiSelect;
      const steps = prevState.steps.map((s) => ({ ...s }));
      steps[1].status = 'current'; // step 2 — incomplete after reset
      steps[2].status = 'disabled'; // step 3 — reset by cascade

      return {
        ...prevState,
        aiSelect: newAiSelect,
        // Step 2 reset → first substep of the chosen subflow ('1a' AI / '2a' manual).
        step2Sub: newAiSelect ? '1a' : '2a',
        selectedName: null,
        selectedDomain: null,
        // The own-name/own-domain flags ARE the manual subflow: true in '2a'
        // (manual), false in '1a' (AI). These persist via own_name/own_domain so
        // the load effect re-derives aiSelect = !(own_name ?? own_domain)
        // correctly across the session save/load round-trip.
        step2SelectBrand: !newAiSelect,
        step2SelectDomain: !newAiSelect,
        showDnsRecords: false,
        // Step 3 + 4 cascade reset (back to the default AI mode, no logo).
        aiSelectLogo: true,
        selectedLogo: null,
        uploadedFile: null,
        steps,
      };
    });
  }, [dispatch]);

  const onStep2SelectDomain = React.useCallback(() => {
    setState((prevState) => {
      return {
        ...prevState,
        step2SelectDomain: true,
        step2Sub: isMobile && prevState.step === 2 ? '2c' : prevState.step2Sub,
      };
    });
  }, [isMobile]);

  const onNameChange = React.useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const value = event.target ? (event.target as HTMLInputElement).value : '';

      setState((prevState) => ({
        ...prevState,
        selectedName: value,
        step2SelectBrand: true,
        selectedDomain: null,
        showDnsRecords: false,
      }));

      dispatch(clearDomainConnect());
    },
    [dispatch],
  );

  const onDomainNameChange = React.useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const value = event.target ? (event.target as HTMLInputElement).value : '';

      const validationResult = validateDomain(value);

      setState((prevState) => ({ ...prevState, selectedDomain: validationResult.cleanValue, showDnsRecords: false }));

      dispatch(clearDomainConnect());
    },
    [dispatch],
  );

  const onRefreshDomainSuggestions = React.useCallback(() => {
    if (isNil(state.selectedName) || isNil(state.selectedCountry)) {
      return;
    }

    dispatch(fetchDomainsSuggestionsByDomain({ query: state.selectedName, limit: 3, country: state.selectedCountry }));
  }, [dispatch, state.selectedName, state.selectedCountry]);

  const onRefreshLogoSuggestions = React.useCallback(() => {
    dispatch(clearLogosSuggestions());
    fetchLogoSuggestions();
  }, [dispatch, fetchLogoSuggestions]);

  const onAiSelectLogoToggle = React.useCallback(() => {
    // Step 3 is a single screen; the toggle switches its AI/manual mode and
    // resets the step (and step 4) — it is not navigation. See §5.
    requestedLogos.current = false;
    requestedThemesSliceStartIndex.current = 0;
    requestedThemesSliceWrapped.current = false;
    dispatch(clearLogosSuggestions());

    setState((prevState) => {
      const steps = prevState.steps.map((s) => ({ ...s }));
      steps[2].status = 'current'; // step 3 — incomplete after reset
      return {
        ...prevState,
        aiSelectLogo: !prevState.aiSelectLogo,
        selectedLogo: null,
        uploadedFile: null,
        steps,
      };
    });
  }, [dispatch]);

  useBrowserHistorySync({
    step: state.step,
    displayIntro: state.displayIntro,
    isMobile,
    aiSelectLogo: state.aiSelectLogo,
    step2Sub: state.step2Sub,
    loading: state.loading,
    onNextStep,
    onPrevStep,
    onStep2Next,
    onStep2SelectDomain,
  });

  const onSelectLogo = React.useCallback((event: React.FormEvent<HTMLInputElement>) => {
    const cdn_url = event?.currentTarget?.dataset?.cdn_url ?? null;

    if (cdn_url) {
      setState((prevState) => {
        const steps = prevState.steps.map((s) => ({ ...s }));
        steps[2].status = 'ok';
        return { ...prevState, selectedLogo: cdn_url, steps };
      });
    }
  }, []);

  const onFinishSetup = React.useCallback(() => {
    if (!isNil(session.sessionId)) {
      // Remember this deploy session so the brand switcher adopts the brand it
      // creates (linked by `deployments[].session_id`) the moment it appears in
      // the list, instead of defaulting to the first brand.
      writePendingBrandSessionId(session.sessionId);
      dispatch(sessionDeploy({ id: session.sessionId }));
    }
  }, [dispatch, session.sessionId]);

  // All logic and handlers related to selecting / uploading a brand logo image file live in this hook.
  const { onSelectFile, onResetSelectedLogo } = useLogoUpload({ setState });

  const domainSuggestionTimeoutId = React.useRef(0);

  React.useEffect(() => {
    // Debounce
    if (!state.aiSelect) {
      if (domainSuggestionTimeoutId.current) {
        clearTimeout(domainSuggestionTimeoutId.current);
      }

      domainSuggestionTimeoutId.current = window.setTimeout(() => {
        if (isNil(state.selectedName) || isNil(state.selectedCountry)) {
          return;
        }

        dispatch(
          fetchDomainsSuggestionsByDomain({ query: state.selectedName, limit: 3, country: state.selectedCountry }),
        );
      }, 500);
    }

    return () => {
      if (domainSuggestionTimeoutId.current) {
        clearTimeout(domainSuggestionTimeoutId.current);
      }
    };
  }, [dispatch, state.selectedName, state.selectedCountry, state.aiSelect]);

  const onToggleDnsRecords = React.useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      showDnsRecords: !prevState.showDnsRecords,
    }));
  }, []);

  const contextValue = React.useMemo(() => {
    return {
      isMobile,
      displayIntro: state.displayIntro,
      step: state.step,
      totalSteps: state.totalSteps,
      steps: state.steps,
      selectedCountry: state.selectedCountry,

      selectedName: state.selectedName,
      selectedDomain: state.selectedDomain,
      aiSelect: state.aiSelect,
      step2SelectDomain: state.step2SelectDomain,
      step2Sub: state.step2Sub,

      selectedLogo: state.selectedLogo,
      aiSelectLogo: state.aiSelectLogo,
      uploadedFile: state.uploadedFile,

      bgDefault: session.data.configuration_data?.tokens?.['--bg-default'],
      tokens: session.data.configuration_data?.tokens,
      iframeUrl: session.data.configuration_data?.previewUrl,

      domainsSuggestionsByCountry: domainsSuggestionsByCountry,
      domainsSuggestionsByDomain: domainsSuggestionsByDomain,
      logosSuggestions: logosSuggestions,

      actions: {
        onNextStep,
        onPrevStep,
        onStep2Next,
        onSelectedCountry,
        onRefreshSuggestions,
        onSelectName,
        onSelectDomain,
        onAiSelectToggle,
        onNameChange,
        onDomainNameChange,
        onRefreshDomainSuggestions,
        onRefreshLogoSuggestions,
        onAiSelectLogoToggle,
        onSelectLogo,
        onFinishSetup,
        onSelectFile,
        onResetSelectedLogo,
        onStep2SelectDomain,
        onConnect,
        onOpenConnect,
        onToggleDnsRecords,
      },

      deployProgress: session.deploy,
      domainConnect: session.domainConnect,
      isConnectOpen,
      showDnsRecords: state.showDnsRecords,

      mlServiceLoading,
      loadingDomainsByCountry,
      loadingDomainsByDomain,
      loadingLogos,

      countries,

      parentContext: dataElementContext,
    };
  }, [
    dataElementContext,
    isMobile,
    state,
    onNextStep,
    onPrevStep,
    onStep2Next,
    onSelectedCountry,
    onRefreshSuggestions,
    onSelectName,
    onSelectDomain,
    onAiSelectToggle,
    onNameChange,
    onRefreshDomainSuggestions,
    onRefreshLogoSuggestions,
    onAiSelectLogoToggle,
    onSelectLogo,
    onFinishSetup,
    domainsSuggestionsByCountry,
    domainsSuggestionsByDomain,
    logosSuggestions,
    mlServiceLoading,
    loadingDomainsByCountry,
    loadingDomainsByDomain,
    loadingLogos,

    onSelectFile,
    onResetSelectedLogo,
    onStep2SelectDomain,
    onDomainNameChange,
    onConnect,
    onOpenConnect,
    isConnectOpen,
    onToggleDnsRecords,

    countries,

    session.data,
    session.deploy,
    session.domainConnect,
  ]);

  console.log('Deploy[contextValue]', contextValue);

  if (state.loading) {
    return <PageLoader />;
  }

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Deploy;
